import type { MapRef } from 'react-map-gl';
import { format0, formatNumber } from 'utils/number';
import type { GroupedWell } from 'components/cross-section/utils';
import { EDITING_GEOMETRY_KIND } from 'components/cross-section/well-symbols';
import type { EditingPoint, SectionLine } from './atoms';
import { FT_PER_DEGREE_LAT, ftPerDegreeLon } from './workspace-map-utils';
import type {
  PreDrillWorkspace,
  PreDrillVerificationItems,
  PreDrillVerificationItem,
  PreDrillColorRule,
  PreDrillFormation,
  PreDrillTypeCurveFormation,
  FormationDepth,
  WellborePoint,
} from 'types/api-responses';
import type {
  ColorByCategory,
  ColorByApplyTarget,
  ColorByOption,
  ColorByRule,
} from 'components/color-by-attribute-modal';
import type { VerificationTask } from 'components/verification-tasks-panel';
import type { FilteredUwi } from 'types/pre-drill-workspace';
import {
  targetZoneConfirmationsPath,
  targetZoneRefreshesPath,
  targetZoneSuggestionsPath,
  type IntersectedGrid,
  type IntersectedGridReason,
  type IntersectedGridsResponse,
} from 'types/wellbore-geometry-workspace';

// Human labels + helper text for the pre-drill verification paths.
const VERIFICATION_LABELS: Record<
  string,
  { label: string; description: string }
> = {
  permit_selection: {
    label: 'Verify permit selection',
    description: 'The permit on file is the right source well.',
  },
  target_zone: {
    label: 'Verify pre-drill target zone',
    description: 'The confirmed target zone is the right interval.',
  },
  collection_selection: {
    label: 'Verify collection selection',
    description: 'The Structure Grid Collection is correct for this area.',
  },
  wellbore_geometry: {
    label: 'Verify pre-drill wellbore geometry',
    description: 'The generated 3D path looks reasonable.',
  },
  final_gate: {
    label: 'Final verification',
    description: 'All upstream checks pass.',
  },
};

// Human labels for the agent's per-rule score keys (Confidence Breakdown table).
// Falls back to a title-cased key.
export const SCORING_RULE_LABELS: Record<string, string> = {
  // Live BE breakdown keys (camelCase).
  operatorArea: 'Operator Area Preference',
  operatorNaming: 'Operator Naming Convention',
  recentDrilling: 'Recent Drilling Activity',
  nearbyWellPatterns: 'Nearby Well Patterns',
  recentPermitActivity: 'Recent Permit Activity',
  // Legacy snake_case keys.
  operator_naming: 'Operator Naming Convention',
  nearby_well_patterns: 'Nearby Well Patterns',
  operator_area: 'Operator Area Preference',
  recent_permit_activity: 'Recent Permit Activity',
  recent_drilling: 'Recent Drilling Activity',
  permit_depth_compatibility: 'Permit Depth Compatibility',
};

// "SRP Well Name": `"<name> #<number>"` (mirrors Well#name on the BE). Returns
// null when neither is present so callers can fall back (e.g. to the UWI).
export function formatSrpWellName(
  name: string | null | undefined,
  number: string | null | undefined
): string | null {
  const wellName = name?.trim() || null;
  const wellNumber = number?.trim() || null;
  if (wellName && wellNumber) return `${wellName} #${wellNumber}`;
  return wellName ?? (wellNumber ? `#${wellNumber}` : null);
}

// Display name for an agent-run / confirmation actor. Manual runs surface as
// "User" (not the BE "Operator" label); automated runs keep their actor name.
export function actorDisplayName(actorType: string, actorName: string): string {
  if (actorType === 'manual') return 'User';
  if (actorName.trim().toLowerCase() === 'operator') return 'User';
  return actorName;
}

// Verdict-able tasks shown in the panel (everything except final_gate).
export function taskVerificationItems(
  items: PreDrillVerificationItems
): PreDrillVerificationItem[] {
  return items.filter((item) => item.path !== 'final_gate');
}

// Map verification items → the shared VerificationTask shape (same verdict
// encoding: boolean | null, true=correct / false=incorrect).
export function toVerificationTasks(
  items: PreDrillVerificationItem[]
): VerificationTask[] {
  return items.map((item) => ({
    id: item.id,
    label: VERIFICATION_LABELS[item.path]?.label ?? item.path,
    description: VERIFICATION_LABELS[item.path]?.description,
    status: item.correctnessState,
    eventsHref: `/verification_items/${item.id}/events`,
    disabled: !item.editable,
    note: item.note ?? undefined,
    lockReason: item.lockReason,
  }));
}

// ── Section visibility (purely client-side chrome) ──────────────────────────
export const PRE_DRILL_SECTIONS = [
  { id: 'details', label: 'Details' },
  { id: 'map', label: 'Map' },
  { id: 'targetZone', label: 'Target Zone' },
  { id: 'subsurfaceExplorer', label: 'Subsurface Explorer' },
  { id: 'wellborePoints', label: 'Wellbore Points' },
  { id: 'relatedFiles', label: 'Related Items' },
] as const;

export type PreDrillSectionId = (typeof PRE_DRILL_SECTIONS)[number]['id'];

export function resolveSectionVisibility(): Record<PreDrillSectionId, boolean> {
  return {
    details: true,
    map: true,
    targetZone: true,
    subsurfaceExplorer: true,
    wellborePoints: true,
    relatedFiles: true,
  };
}

// ── Color rules: map between the persisted schema (filterId / targets[] /
// primaryColor) and the modal's ColorByRule (attribute / applyTo{}). ──
export function readColorRules(workspace: PreDrillWorkspace): ColorByRule[] {
  const rules = workspace.state.colorRules ?? [];
  return rules.map((rule) => ({
    ruleId: rule.ruleId,
    attribute: rule.filterId,
    selectedValues: rule.selectedValues,
    selectedValueLabels: rule.selectedValueLabels ?? [],
    valueColors: rule.valueColors,
    applyTo: Object.fromEntries(
      PRE_DRILL_COLOR_TARGETS.map((t) => [t.id, rule.targets.includes(t.id)])
    ),
    enabled: rule.enabled,
  }));
}

export function toColorRuleState(rules: ColorByRule[]): PreDrillColorRule[] {
  return rules.map((rule) => {
    const targets = Object.entries(rule.applyTo)
      .filter(([, on]) => on)
      .map(([key]) => key);
    const firstColor = Object.values(rule.valueColors)[0] ?? '#9e9e9e';
    return {
      ruleId: rule.ruleId,
      enabled: rule.enabled,
      filterId: rule.attribute,
      selectedValues: rule.selectedValues,
      selectedValueLabels: rule.selectedValueLabels,
      valueColors: rule.valueColors,
      targets,
      primaryColor: firstColor,
    };
  });
}

// Color-by attributes + surfaces (consumed by the shared ColorByAttributeModal).
export const PRE_DRILL_COLOR_CATEGORIES: ColorByCategory[] = [
  { id: 'status', label: 'Well Status' },
  { id: 'wellQualifier', label: 'Well Qualifier' },
  { id: 'holeDirection', label: 'Hole Direction' },
  { id: 'currentOperator', label: 'Current Operator' },
  { id: 'permitDate', label: 'Permit Date' },
  { id: 'spudDate', label: 'Spud Date' },
  { id: 'completionDate', label: 'Completion Date' },
  { id: 'firstProductionDate', label: 'First Production Date' },
  { id: 'srpLandingZone', label: 'SIM Landing Zone' },
  { id: 'typeCurveFormation', label: 'Type Curve Formation' },
];

// Well Qualifier is the only static color attribute (subject vs nearby); every
// other attribute's options come from the live facets atom.
const PRE_DRILL_COLOR_OPTIONS: Record<string, ColorByOption[]> = {
  wellQualifier: ['Subject Wellbore', 'Nearby Wells'].map((v) => ({
    id: v,
    name: v,
  })),
};

export function getPreDrillColorOptions(categoryId: string): ColorByOption[] {
  return PRE_DRILL_COLOR_OPTIONS[categoryId] ?? [];
}

/** A `/queries/wells` `boundingBox`: the SW/NE viewport corners (the shape the
 *  color-by picker + paint calls send). */
export interface FacetsBoundingBox {
  swLng: number;
  swLat: number;
  neLng: number;
  neLat: number;
}

// The Map's viewport as the wells endpoint's `boundingBox` (SW/NE); undefined
// when the map isn't ready (caller falls back to a neighborhood query).
export function boundingBoxFromMapRef(
  mapRef: MapRef | null
): FacetsBoundingBox | undefined {
  const bounds = mapRef?.getBounds?.();
  if (!bounds) return undefined;

  // Prefer the array form: `[[swLng, swLat], [neLng, neLat]]`.
  const arr =
    typeof (bounds as { toArray?: () => number[][] }).toArray === 'function'
      ? (bounds as { toArray: () => number[][] }).toArray()
      : Array.isArray(bounds)
        ? (bounds as unknown as number[][])
        : null;
  if (arr && arr.length === 2 && arr[0]?.length === 2 && arr[1]?.length === 2) {
    const [[swLng, swLat], [neLng, neLat]] = arr;
    return { swLng, swLat, neLng, neLat };
  }

  // Fallback: the LngLat object form.
  const sw = bounds.getSouthWest?.();
  const ne = bounds.getNorthEast?.();
  if (!sw || !ne) return undefined;
  return { swLng: sw.lng, swLat: sw.lat, neLng: ne.lng, neLat: ne.lat };
}

/** A formation band ready to draw: top + (extended) base in TVDSS. */
export interface FormationBand {
  gridId: number;
  name: string;
  /** Geologic interval name — right-margin band label (falls back to name). */
  geologicInterval: string | null;
  color: string;
  topTvdss: number;
  bottomTvdss: number;
}

// A formation depth is a scalar or a 2D [evalPoint][slice] grid; collapse it to
// a single band depth (center slice of the first eval point).
function scalarDepth(value: FormationDepth): number | null {
  if (typeof value === 'number') return value;
  if (!Array.isArray(value)) return null;
  const row = value[0] ?? [];
  const mid = row[Math.floor(row.length / 2)];
  return typeof mid === 'number' ? mid : null;
}

export function buildFormationBands(
  formations: PreDrillFormation[]
): FormationBand[] {
  const withTop = formations
    .map((f) => ({
      gridId: f.gridId,
      name: f.name,
      geologicInterval: f.geologicInterval,
      color: f.displayColor ?? '#c6c6c6',
      top: scalarDepth(f.topTvdss),
      base: scalarDepth(f.baseTvdss),
    }))
    .filter((f): f is typeof f & { top: number } => f.top !== null);
  const sorted = [...withTop].sort((a, b) => b.top - a.top);
  return sorted.map((f, index) => {
    const next = sorted[index + 1];
    return {
      gridId: f.gridId,
      name: f.name,
      geologicInterval: f.geologicInterval,
      color: f.color,
      topTvdss: f.top,
      // Extend down to the next formation's top; deepest keeps its own base.
      bottomTvdss: next ? next.top : (f.base ?? f.top),
    };
  });
}

/** A type-curve formation name to draw as a left-margin label at its mid-depth. */
export interface TypeCurveFormationLabel {
  name: string;
  color: string;
  midTvdss: number;
}

// Project each type-curve formation to a mid-TVDSS label position (top/base
// midpoint), mirroring the gun barrel's left-margin TC names.
export function buildTypeCurveFormationLabels(
  formations: PreDrillTypeCurveFormation[]
): TypeCurveFormationLabel[] {
  const labels: TypeCurveFormationLabel[] = [];
  for (const tc of formations) {
    const top = scalarDepth(tc.topTvdss);
    const base = scalarDepth(tc.baseTvdss);
    if (top === null || base === null) continue;
    labels.push({
      name: tc.name,
      color: tc.displayColor ?? '#161616',
      midTvdss: (top + base) / 2,
    });
  }
  return labels;
}

// ── 2D Profile: formations that follow the structure across the section ──────
// Read the on-wellbore (center) slice column → one depth per eval point. Scalars
// are constant; missing samples become NaN so SciChart breaks the line (gap).
function depthColumn(
  value: FormationDepth,
  count: number,
  centerSlice: number
): number[] {
  if (typeof value === 'number') return new Array(count).fill(value);
  if (!Array.isArray(value)) return new Array(count).fill(NaN);
  return Array.from({ length: count }, (_, i) => {
    const sample = value[i]?.[centerSlice];
    return typeof sample === 'number' ? sample : NaN;
  });
}

const finiteAvg = (values: number[]): number => {
  const finite = values.filter((n) => Number.isFinite(n));
  if (finite.length === 0) return Number.NEGATIVE_INFINITY;
  return finite.reduce((sum, n) => sum + n, 0) / finite.length;
};

/** A structure-grid formation as a filled band across the vertical section. */
export interface ProfileFormationBand {
  gridId: number;
  name: string;
  geologicInterval: string | null;
  color: string;
  xs: number[];
  tops: number[];
  bases: number[];
}

// Structure-grid formations as filled bands across the section, stacked so each
// grid fills down to the next formation's top (deepest keeps its base).
export function buildProfileFormationBands(
  formations: PreDrillFormation[],
  vsValues: number[],
  centerSlice: number
): ProfileFormationBand[] {
  const count = vsValues.length;
  // Eval points arrive in wellbore order (VS non-monotonic). SciChart bands need
  // ascending X, so sort by VS and apply the same permutation to every depth.
  const order = vsValues
    .map((_, i) => i)
    .sort((a, b) => vsValues[a] - vsValues[b]);
  const sortedXs = order.map((i) => vsValues[i]);

  const withTop = formations
    .map((f) => ({
      gridId: f.gridId,
      name: f.name,
      geologicInterval: f.geologicInterval,
      color: f.displayColor ?? '#c6c6c6',
      tops: depthColumn(f.topTvdss, count, centerSlice),
      bases: depthColumn(f.baseTvdss, count, centerSlice),
    }))
    .filter((f) => f.tops.some((v) => Number.isFinite(v)))
    .sort((a, b) => finiteAvg(b.tops) - finiteAvg(a.tops));

  return withTop.map((f, index) => {
    const next = withTop[index + 1];
    // Each base extends to the next formation's top (deepest keeps its own),
    // computed in eval-point order then reordered by VS below.
    const bases = f.tops.map((top, i) => {
      const nextTop = next?.tops[i];
      if (next && Number.isFinite(nextTop)) return nextTop;
      const ownBase = f.bases[i];
      return Number.isFinite(ownBase) ? ownBase : top;
    });
    return {
      gridId: f.gridId,
      name: f.name,
      geologicInterval: f.geologicInterval,
      color: f.color,
      xs: sortedXs,
      tops: order.map((i) => f.tops[i]),
      bases: order.map((i) => bases[i]),
    };
  });
}

/** A type-curve formation as a filled band across the vertical section. */
export interface ProfileTypeCurveBand {
  name: string;
  color: string;
  xs: number[];
  tops: number[];
  bases: number[];
}

// Type-curve formations as filled bands (top→base per eval point) — the
// landing-zone fills in the reference 2D Profile.
export function buildProfileTypeCurveBands(
  formations: PreDrillTypeCurveFormation[],
  vsValues: number[],
  centerSlice: number
): ProfileTypeCurveBand[] {
  const count = vsValues.length;
  // Same VS ordering as the SG bands so SciChart draws left→right.
  const order = vsValues
    .map((_, i) => i)
    .sort((a, b) => vsValues[a] - vsValues[b]);
  const sortedXs = order.map((i) => vsValues[i]);
  return formations
    .map((tc) => {
      const tops = depthColumn(tc.topTvdss, count, centerSlice);
      const bases = depthColumn(tc.baseTvdss, count, centerSlice);
      return {
        name: tc.name,
        color: tc.displayColor ?? '#c6c6c6',
        xs: sortedXs,
        tops: order.map((i) => tops[i]),
        bases: order.map((i) => bases[i]),
      };
    })
    .filter((tc) => tc.tops.some((v) => Number.isFinite(v)));
}

export const PRE_DRILL_COLOR_TARGETS: ColorByApplyTarget[] = [
  { id: 'map', label: 'Map' },
  { id: 'gunBarrel', label: 'Gun Barrel' },
  { id: 'profile2d', label: '2D Profile' },
];

// Neighborhood radius (miles) for color-by paint calls — same scope as the
// profile2D wells call, so membership matches the facet value list.
export const COLOR_BY_RADIUS = '100';

// Map each colorable attribute (except Well Qualifier) to the snake_case wells
// field, for the facets request + paint params. Response keyed by camelCase id.
export const COLOR_ATTRIBUTE_FACET_PARAM: Record<string, string> = {
  status: 'status',
  currentOperator: 'current_operator',
  holeDirection: 'hole_direction',
  srpLandingZone: 'srp_landing_zone',
  typeCurveFormation: 'type_curve_formation',
  permitDate: 'permit_date',
  spudDate: 'spud_date',
  completionDate: 'completion_date',
  firstProductionDate: 'first_production_date',
};

// Full `facets` list sent on the bbox-scoped value-picker call — every
// colorable attribute's snake_case field. Response keyed by camelCase id.
export const COLOR_ATTRIBUTE_FACET_FIELDS = Object.values(
  COLOR_ATTRIBUTE_FACET_PARAM
);

// Sentinel the wells index assigns to documents with no `status` /
// `hole_direction` (mapped with `null_value: 'NULL'`). The facet picker shows it
// as a selectable bucket, so a null-valued well must match when this bucket is
// chosen — keep this token identical to the index's `null_value`.
export const FACET_NULL_SENTINEL = 'NULL';

// Date color-by attributes: the picker lists calendar-quarter buckets, and each
// well carries a BE-stamped `<attr>Quarter` sibling (e.g. `permitDateQuarter`)
// that already holds the quarter label. Membership matches on that sibling — the
// raw `<attr>` date is left untouched for display. (BE owns the quarter
// derivation; the FE does no date bucketing.)
export const PRE_DRILL_DATE_COLOR_ATTRIBUTES = new Set([
  'permitDate',
  'spudDate',
  'completionDate',
  'firstProductionDate',
]);

// Groups the cached filtered wells by a single color-by attribute value,
// returning the 10-digit UWIs that paint for that value. Term attributes match
// their raw field; date attributes match their `<attr>Quarter` sibling (the
// quarter label, identical to the facet bucket id). A null/undefined value
// matches only when the selected value is the `FACET_NULL_SENTINEL` bucket,
// mirroring how `status` and `hole_direction` are indexed with `null_value`.
export function resolveColorByUwiMembership(
  wells: FilteredUwi[],
  category: string,
  value: string
): string[] {
  // Only the colorable attributes map to a well field; guard so a non-color-by
  // key (e.g. `name`, `wellQualifier`, `uwi`) can never be matched on.
  if (!(category in COLOR_ATTRIBUTE_FACET_PARAM)) return [];
  const field = PRE_DRILL_DATE_COLOR_ATTRIBUTES.has(category)
    ? `${category}Quarter`
    : category;
  return wells
    .filter((well) => {
      const raw = well[field as keyof FilteredUwi];
      if (raw === null || raw === undefined) {
        return value === FACET_NULL_SENTINEL;
      }
      return raw === value;
    })
    .map((well) => well.uwi);
}

// The surfaces a color rule can paint — the Apply-To target ids.
export type PreDrillColorTarget = 'map' | 'gunBarrel' | 'profile2d';

// Color for a bore UWI: newest-first among enabled rules targeting the surface.
// Well Qualifier resolves via subject/nearby; others via the UWI map.
export function colorForBoreUwi(
  uwi: string | null | undefined,
  subjectUwi: string,
  rules: ColorByRule[],
  uwiMap: Record<string, string[]>,
  target: PreDrillColorTarget = 'map'
): string | undefined {
  // A bore without a UWI can't match any rule (Well Qualifier or membership).
  if (!uwi) return undefined;
  const targetRules = rules.filter((r) => r.enabled && r.applyTo[target]);
  for (let i = targetRules.length - 1; i >= 0; i -= 1) {
    const rule = targetRules[i];
    if (rule.attribute === WELL_QUALIFIER_ATTRIBUTE) {
      const bucket =
        uwi === subjectUwi ? WELL_QUALIFIER_SUBJECT : WELL_QUALIFIER_NEARBY;
      if (rule.selectedValues.includes(bucket) && rule.valueColors[bucket]) {
        return rule.valueColors[bucket];
      }
      continue;
    }
    // `filteredUwis` membership is keyed by 10-digit UWI; bores carry uwi14,
    // so match on the 10-digit prefix.
    const uwi10 = uwi.slice(0, 10);
    for (const value of rule.selectedValues) {
      if (uwiMap[`${rule.attribute}:${value}`]?.includes(uwi10)) {
        if (rule.valueColors[value]) return rule.valueColors[value];
      }
    }
  }
  return undefined;
}

// Build a `uwi → hex` map for the scoped-wells layer from enabled map-targeting
// rules + the filtered-UWI map; last matching rule wins.
export function buildColorByUwiColorMap(
  rules: ColorByRule[],
  uwiMap: Record<string, string[]>
): Record<string, string> {
  const mapRules = rules.filter((r) => r.enabled && r.applyTo.map);
  const uwiColorMap: Record<string, string> = {};
  for (const rule of mapRules) {
    for (const value of rule.selectedValues) {
      const color = rule.valueColors[value];
      if (!color) continue;
      const uwis = uwiMap[`${rule.attribute}:${value}`];
      if (!uwis) continue;
      for (const uwi of uwis) uwiColorMap[uwi] = color;
    }
  }
  return uwiColorMap;
}

// Deterministic hash of the tile URLs so Source/Layer ids change with
// membership (forces Mapbox to drop stale tiles).
export function tileFingerprint(tiles: string[]): string {
  let hash = 0;
  const str = tiles.join('|');
  for (let i = 0; i < str.length; i += 1) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash).toString(36);
}

// Only Well Qualifier drives the Gun Barrel + 2D Profile; every other attribute
// is Map-only (the modal disables those targets).
export const WELL_QUALIFIER_ATTRIBUTE = 'wellQualifier';
const WELL_QUALIFIER_SUBJECT = 'Subject Wellbore';
const WELL_QUALIFIER_NEARBY = 'Nearby Wells';

// The enabled Well-Qualifier rule (the only cross-surface attribute), if any.
export function activeWellQualifierRule(
  rules: ColorByRule[]
): ColorByRule | null {
  return (
    rules.find((r) => r.enabled && r.attribute === WELL_QUALIFIER_ATTRIBUTE) ??
    null
  );
}

export interface WellQualifierColors {
  subject?: string;
  nearby?: string;
}

// Resolve the subject / nearby swatches from a Well-Qualifier rule.
export function wellQualifierColors(
  rule: ColorByRule | null
): WellQualifierColors {
  if (!rule) return {};
  return {
    subject: rule.valueColors[WELL_QUALIFIER_SUBJECT],
    nearby: rule.valueColors[WELL_QUALIFIER_NEARBY],
  };
}

export const PRE_DRILL_COLOR_NEW_RULE_APPLY_TO = {
  map: true,
  profile2d: false,
  gunBarrel: false,
};

export const PRE_DRILL_COLOR_RESET_APPLY_TO = {
  map: false,
  profile2d: false,
  gunBarrel: false,
};

// Format a 0–1 score as a whole-number percentage (suggestion tiles / breakdown).
export function pctScore(score: number): string {
  return `${formatNumber(score * 100, 0, 0)}%`;
}

// ── Subsurface Explorer: TVD-axis ground datum ──────────────────────────────
// The charts plot in TVDSS and label the left axis as TVD via `datum − TVDSS`
// (see subsurface-chart-axes). TVDSS + TVD is invariant along the bore, so any
// aligned (TVDSS, TVD) sample recovers the ground/KB elevation datum. Returns
// null when no finite pair is available — the caller then leaves the datum at
// its default (0), where TVD would read identically to TVDSS.
export function groundElevationDatumFt(
  tvdssFt: number | null | undefined,
  tvdFt: number | null | undefined
): number | null {
  if (
    typeof tvdssFt === 'number' &&
    Number.isFinite(tvdssFt) &&
    typeof tvdFt === 'number' &&
    Number.isFinite(tvdFt)
  ) {
    return tvdssFt + tvdFt;
  }
  return null;
}

// 2D Profile datum from the first aligned (TVDSS, TVD) sample. Prefers the
// wellbore-trajectory series (equal-length, non-empty) and falls back to the
// reference profile series; null when neither yields a finite pair.
export function profile2dGroundDatumFt(
  trajTvdss: readonly number[],
  trajTvd: readonly number[],
  profileTvdss: readonly number[],
  profileTvd: readonly number[]
): number | null {
  if (trajTvd.length > 0 && trajTvd.length === trajTvdss.length) {
    const datum = groundElevationDatumFt(trajTvdss[0], trajTvd[0]);
    if (datum !== null) return datum;
  }
  if (profileTvd.length > 0 && profileTvdss.length > 0) {
    return groundElevationDatumFt(profileTvdss[0], profileTvd[0]);
  }
  return null;
}

// ── Story 8: permitted depth against the intersected grids ───────────────────

/**
 * Whether the permitted-depth indicator can be shown at all. The ranges are worth
 * reading on their own, so a response that cannot place the permit still renders its
 * bands; only the pill is withheld.
 * @param {IntersectedGridsResponse | null} grids The intersected grids read.
 * @return {boolean} True when the indicator may render.
 */
export const permittedDepthIndicatorApplies = (
  grids: IntersectedGridsResponse | null
): boolean => !!grids && grids.applicable;

/**
 * The pill's tooltip. Named with the depth when the permit carries one, since the
 * figure is the whole point of the indicator.
 * @param {IntersectedGridsResponse | null} grids The intersected grids read.
 * @return {string} The tooltip.
 */
export const permittedDepthTooltip = (
  grids: IntersectedGridsResponse | null
): string => {
  const depth = grids?.permittedDepthTvdFt;
  if (depth === null || depth === undefined) {
    return 'The permitted depth falls in this interval';
  }
  return `Permitted TVD ${formatNumber(depth, 0, 0)} ft falls in this interval`;
};

/**
 * Where the permitted depth sits when the indicator applies but no interval claims it,
 * stated so it can't be mistaken for the indicator being off.
 * @param {IntersectedGridsResponse | null} grids The intersected grids read.
 * @return {string | null} The sentence, or null when an interval does carry the depth.
 */
export const permittedDepthOutsideSection = (
  grids: IntersectedGridsResponse | null
): string | null => {
  if (!grids || !grids.applicable) return null;
  if (grids.grids.some((grid) => grid.containsPermittedDepth === true))
    return null;

  const floor = grids.permitFloorTvdssFt;
  const banded = grids.grids.filter(
    (grid) => grid.minTvdssFt !== null && grid.maxTvdssFt !== null
  );
  if (floor === null || banded.length === 0) return null;

  // Subsea depths run negative, so the deepest band is the smallest minimum.
  const deepest = Math.min(...banded.map((grid) => grid.minTvdssFt as number));
  const shallowest = Math.max(
    ...banded.map((grid) => grid.maxTvdssFt as number)
  );
  const depth =
    grids.permittedDepthTvdFt === null
      ? 'The permitted depth'
      : `Permitted TVD ${formatNumber(grids.permittedDepthTvdFt, 0, 0)} ft`;
  const subsea = `${formatNumber(floor, 0, 0)} ft TVDSS`;

  if (floor < deepest) {
    return `${depth} (${subsea}) is below every interval catalogued at this well, so none of them contains it.`;
  }
  if (floor > shallowest) {
    return `${depth} (${subsea}) is above every interval catalogued at this well, so none of them contains it.`;
  }
  // Between two picks rather than outside the section — a gap in the catalogue here.
  return `${depth} (${subsea}) falls between two catalogued intervals at this well, so none of them contains it.`;
};

/**
 * Why a response carries no indicator, or a grid carries no range, in words. The
 * backend names every withholding rather than leaving a blank to be inferred.
 * @param {IntersectedGridReason | null} reason The reason, as the backend names it.
 * @return {string | null} The sentence, or null when there is nothing to say.
 */
export const intersectedGridReasonText = (
  reason: IntersectedGridReason | null
): string | null => {
  switch (reason) {
    case 'missing_geometry':
      return 'This wellbore has fewer than two points, so it has no ground track to sample the grids along.';
    case 'missing_permitted_depth_tvd_ft':
      return 'This wellbore has no permitted depth, so there is nothing to place against the grids.';
    case 'missing_datum':
      return 'No datum resolves for this wellbore, so the permitted depth cannot be expressed subsea.';
    case 'outside_grid_extent':
      return 'This grid does not cover this well.';
    case 'no_grid_value_along_bore':
      return 'This grid covers the well but has no value along the bore.';
    default:
      return null;
  }
};

/**
 * One end of a grid's band under this bore. Bare numbers: the column header carries
 * the unit, and an em dash says the grid has no range here.
 * @param {number | null} value The subsea depth.
 * @return {string} The printable value.
 */
export const formatGridTvdss = (value: number | null): string =>
  format0(value, '—');

/**
 * A depth or elevation in whole feet with its unit; an em dash when unknown, since a
 * missing depth is never shown as zero.
 * @param {number | null | undefined} value The value in feet.
 * @return {string} The printable value.
 */
export const formatFeet = (value: number | null | undefined): string =>
  formatNumber({ value, minPrecision: 0, unit: 'ft', fallback: '—' });

/**
 * The grids in the order the table reads them: stratigraphic, top down. A grid with
 * no index sorts last rather than jumping the order.
 * @param {IntersectedGrid[]} grids The grids as served.
 * @return {IntersectedGrid[]} The ordered grids.
 */
export const orderGridsStratigraphically = (
  grids: IntersectedGrid[]
): IntersectedGrid[] =>
  [...grids].sort(
    (left, right) =>
      (left.positionalIndex ?? Number.MAX_SAFE_INTEGER) -
      (right.positionalIndex ?? Number.MAX_SAFE_INTEGER)
  );

/**
 * Where the map's working line crosses A–A′, as preview crossings (UAT 35): each at its
 * distance from A, at a depth interpolated from the depths its points last read.
 * @param {SectionLine} line The section's two ends, A and A′.
 * @param {EditingPoint[]} editing The working line, surface hole first.
 * @param {string} uwi14 The bore being edited.
 * @return {GroupedWell[]} One crossing per place the line crosses the section.
 */
export const editingCrossings = (
  line: SectionLine,
  editing: EditingPoint[],
  uwi14: string
): GroupedWell[] => {
  const lonScale = ftPerDegreeLon(line.a[1]);
  const toFt = (lon: number, lat: number): [number, number] => [
    (lon - line.a[0]) * lonScale,
    (lat - line.a[1]) * FT_PER_DEGREE_LAT,
  ];
  const [ax, ay] = toFt(line.b[0], line.b[1]);
  const lengthFt = Math.hypot(ax, ay);
  if (lengthFt === 0) return [];

  const crossings: GroupedWell[] = [];
  for (let i = 0; i < editing.length - 1; i += 1) {
    const from = editing[i];
    const to = editing[i + 1];
    const [px, py] = toFt(from.lon, from.lat);
    const [qx, qy] = toFt(to.lon, to.lat);
    const dx = qx - px;
    const dy = qy - py;
    // Section: A + t·(A′−A); segment: P + u·(Q−P). Parallel segments never cross.
    const cross = ax * dy - ay * dx;
    if (cross === 0) continue;
    const t = (px * dy - py * dx) / cross;
    const u = (px * ay - py * ax) / cross;
    if (t < 0 || t > 1 || u < 0 || u > 1) continue;
    const between = (start: number | null, end: number | null) => {
      if (start === null || end === null) return null;
      return start + (end - start) * u;
    };
    crossings.push({
      id: null,
      uwi: uwi14.slice(0, 10),
      uwi14,
      xOffsetFt: t * lengthFt,
      tvdssFt: between(from.tvdssFt, to.tvdssFt),
      tvdFt: between(from.tvdFt, to.tvdFt),
      surfaceElevationFt: null,
      isVertical: false,
      crossingIndex: crossings.length,
      status: null,
      geometrySourceKind: EDITING_GEOMETRY_KIND,
      workspaceEdited: true,
      color: null,
      header: { name: null, operator: null, landingZone: null, spudDate: null },
    });
  }
  return crossings;
};

/** The addresses the Target Zone section writes to. */
export interface TargetZoneHrefs {
  suggestions: string;
  refreshes: string;
  confirmations: string;
}

/**
 * Under a Well Workspace the target-zone calls go to the bore's own address; the Well
 * page's own workspace keeps the hrefs its show hands it. Auth stays the show's.
 * @param {PreDrillWorkspace} workspace The geometry workspace's show.
 * @param {number | null | undefined} wellWorkspaceId The hosting Well Workspace, if any.
 * @return {TargetZoneHrefs} The three addresses.
 */
export const targetZoneHrefsFor = (
  workspace: PreDrillWorkspace,
  wellWorkspaceId: number | null | undefined
): TargetZoneHrefs => {
  if (!wellWorkspaceId) {
    return {
      suggestions: workspace.resources.targetZoneSuggestions.href,
      refreshes: workspace.resources.targetZoneRefreshes.href,
      confirmations: workspace.resources.targetZoneConfirmations.href,
    };
  }
  return {
    suggestions: targetZoneSuggestionsPath(wellWorkspaceId, workspace.uwi14),
    refreshes: targetZoneRefreshesPath(wellWorkspaceId, workspace.uwi14),
    confirmations: targetZoneConfirmationsPath(
      wellWorkspaceId,
      workspace.uwi14
    ),
  };
};

/**
 * Reads a typed number, tolerating the separators a field prints back.
 * @param {string} value The field as typed.
 * @return {number | null} The number, or null when blank or unparseable.
 */
export const parseTypedNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const number = Number(trimmed.replace(/,/g, ''));
  return Number.isFinite(number) ? number : null;
};

/**
 * A stored value as a field seeds it, trimmed to the precision the table shows; the
 * raw float can carry fourteen digits, which read as noise.
 * @param {number | null} value The value.
 * @param {number} decimals The decimals to keep.
 * @return {string} The field text, blank when not on record.
 */
export const toDecimalField = (
  value: number | null,
  decimals: number
): string => (value === null ? '' : String(Number(value.toFixed(decimals))));

/**
 * What to call a point: a vertex with no role is named by its place in the path.
 * @param {WellborePoint} point The point.
 * @return {string} The label.
 */
export const wellborePointName = (point: WellborePoint): string => {
  if (point.pointType !== 'unassigned') return point.name;
  return `Point ${point.positionalIndex}`;
};

// UAT item 10 (INS-13166's rule): a Landing Point move this far along the hole is
// confirmed before it is written.
export const LANDING_MOVE_CONFIRM_FT = 1_000;

/**
 * Feet as the confirm banner says them: whole, comma-grouped.
 * @param {number} value The value.
 * @return {string} The printable value.
 */
const roundFeet = (value: number): string => `${format0(value)} ft`;

/**
 * Lateral length rounded to the hundred, as an "about" figure.
 * @param {number} value The value.
 * @return {string} The printable value.
 */
const aboutFeet = (value: number): string =>
  `about ${format0(Math.round(value / 100) * 100)} ft`;

/**
 * Why moving the Landing Point from one row to another needs confirming, as the banner
 * says it; null when it can be written at once. It needs confirming when it moves more
 * than 1,000 ft MD, or when the new point sits above the lateral's start (the first
 * step of 80 degrees or more). The lateral is read as the bottom hole's MD less the LP's.
 * @param {WellborePoint[]} points The points, surface hole first.
 * @param {number} fromIndex The current Landing Point, elected or default.
 * @param {number} toIndex The picked row.
 * @param {number | null} firstHorizontalIndex Where the lateral starts.
 * @return {string | null} The question, or null.
 */
export const landingMoveQuestion = (
  points: WellborePoint[],
  fromIndex: number,
  toIndex: number,
  firstHorizontalIndex: number | null
): string | null => {
  const from = points[fromIndex];
  const to = points[toIndex];
  if (!from || !to || fromIndex === toIndex) return null;
  const fromMd = from.measuredDepthFt;
  const toMd = to.measuredDepthFt;
  const farMove =
    fromMd !== null &&
    toMd !== null &&
    Math.abs(toMd - fromMd) > LANDING_MOVE_CONFIRM_FT;
  const aboveHorizontal =
    firstHorizontalIndex !== null && toIndex < firstHorizontalIndex;
  if (!farMove && !aboveHorizontal) return null;

  if (fromMd === null || toMd === null) {
    return `Move the Landing Point from ${wellborePointName(
      from
    )} to ${wellborePointName(to)}?`;
  }
  const move = `Move the Landing Point from ${roundFeet(
    fromMd
  )} MD to ${roundFeet(toMd)} MD?`;
  const toeMd = points[points.length - 1]?.measuredDepthFt ?? null;
  if (toeMd === null) return move;
  return `${move} This changes the lateral length from ${aboutFeet(
    toeMd - fromMd
  )} to ${aboutFeet(toeMd - toMd)}.`;
};
